import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Spinner, 
  Alert, Modal, Form, InputGroup, Card, Badge 
} from 'react-bootstrap';
import { 
  FaUserShield, FaEnvelope, FaKey, 
  FaHistory, FaCalendarAlt, FaEdit 
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import bcrypt from 'bcryptjs';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/admins`, {
        headers: { 'x-auth-token': token }
      });
      
      setAdmins(response.data);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError(err.response?.data?.error || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsResetting(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const salt = await bcrypt.genSalt(10);
      const hashedPassword = await bcrypt.hash(newPassword, salt);

      await axios.put(`${process.env.REACT_APP_API_URL}/admin/admins/${selectedAdmin._id}/password`,
        { password: hashedPassword },
        { headers: { 'x-auth-token': token } }
      );
      
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getRoleBadge = (role) => {
    const variants = {
      superadmin: 'danger',
      admin: 'warning',
      manager: 'primary'
    };
    return <Badge bg={variants[role] || 'secondary'}>{role}</Badge>;
  };

  return (
    <div className="admin-management-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Admin Management</h2>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Last Login</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaUserShield className="me-2" />
                        <div>
                          <div>{admin.name}</div>
                          <small className="text-muted">ID: {admin._id}</small>
                        </div>
                      </div>
                    </td>
                    <td>{getRoleBadge(admin.role)}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaEnvelope className="me-2" />
                        {admin.email}
                      </div>
                    </td>
                    <td>
                      {admin.lastLogin ? (
                        <div className="d-flex align-items-center">
                          <FaHistory className="me-2" />
                          {formatDate(admin.lastLogin)}
                        </div>
                      ) : 'Never'}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaCalendarAlt className="me-2" />
                        {formatDate(admin.createdAt)}
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setShowPasswordModal(true);
                        }}
                      >
                        <FaKey className="me-1" />
                        Reset Password
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Password Reset Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password for {selectedAdmin?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaKey />
                </InputGroup.Text>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaKey />
                </InputGroup.Text>
                <Form.Control
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
              </InputGroup>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handlePasswordReset}
            disabled={isResetting || !newPassword || !confirmPassword}
          >
            {isResetting ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Reset Password'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminManagement;